import { useEffect, useState } from 'react';
import { api } from '../api';
import { ApiError } from '../api/types';
import { existe, idiomaAtual, t } from '../i18n/idioma';
import './botoesSociais.css';

/** Se o servidor tem o "Entrar com Discord" ligado: perguntado uma vez por carga da página. */
let ativoCache: Promise<boolean> | null = null;
function discordAtivo(): Promise<boolean> {
  ativoCache ??= api.discordLoginAtivo().then(r => r.ativo).catch(() => false);
  return ativoCache;
}

/**
 * Entrar ou criar conta com o Discord (techspec-discord-cargos §03). O botão pede a URL ao servidor e troca a página
 * para o Discord; a volta abre a sessão (ou traz o erro em `?discordLogin=erro`). No mesmo padrão do botão do Google
 * (botoesSociais.css): o quadrado azul à esquerda tem um balão de conversa, não a marca do Discord.
 */
export function BotaoDiscord({ texto, desligado, serial, onErro }: {
  texto: 'entrar' | 'criar';
  desligado?: boolean;
  /** O convite digitado (fase restrita): vai junto, como no Google. */
  serial?: string;
  onErro(mensagem: string): void;
}) {
  const [ativo, setAtivo] = useState(false);
  const [indo, setIndo] = useState(false);

  useEffect(() => {
    let vivo = true;
    void discordAtivo().then(a => { if (vivo) setAtivo(a); });
    return () => { vivo = false; };
  }, []);

  if (!ativo) return null;

  const ir = async () => {
    if (indo || desligado) return;
    setIndo(true);
    try {
      const { url } = await api.discordEntrar(serial ? { serial, idioma: idiomaAtual() } : { idioma: idiomaAtual() });
      // Só a URL do Discord (ou a volta do mock, na mesma origem).
      if (url.startsWith('https://discord.com/') || url.startsWith('/')) window.location.assign(url);
      else setIndo(false);
    } catch (e) {
      setIndo(false);
      onErro(e instanceof ApiError ? e.message : t('comum.semRede'));
    }
  };

  return (
    <button type="button" className="botao-discord" disabled={desligado || indo} onClick={() => { void ir(); }}>
      <span className="botao-discord__ico" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V16h0A2.5 2.5 0 0 1 4 13.5z" /><path d="M8.5 10h.01M12 10h.01M15.5 10h.01" /></svg>
      </span>
      <span className="botao-discord__txt">
        {indo ? t('entrada.discordIndo') : t(texto === 'entrar' ? 'entrada.discordEntrar' : 'entrada.discordCriar')}
      </span>
    </button>
  );
}

/**
 * O erro que a volta do Discord deixou na URL para a tela de entrada (`?discordLogin=erro&motivo=<código>`, ou o
 * `?discord=erro` do vincular quando a sessão caiu no caminho). Lido uma vez e apagado. O código só vira texto se
 * tiver o formato de chave e existir no catálogo; senão, o genérico.
 */
export function lerErroDoDiscordNaEntrada(): string | null {
  try {
    const q = new URLSearchParams(window.location.search);
    const login = q.get('discordLogin');
    const vincular = q.get('discord');
    if (login !== 'erro' && vincular !== 'erro') return null;
    const motivo = q.get('motivo') ?? '';
    ['discordLogin', 'discord', 'motivo'].forEach(k => q.delete(k));
    const resto = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (resto ? `?${resto}` : '') + window.location.hash);
    if (login === 'erro' && /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+){1,3}$/.test(motivo) && existe(motivo)) return t(motivo);
    if (vincular === 'erro' && /^[a-zA-Z]{1,32}$/.test(motivo) && existe(`config.discord.retorno.${motivo}`))
      return t(`config.discord.retorno.${motivo}`);
    return t('auth.discord.recusado');
  } catch {
    return null;
  }
}
